import { Decoder } from 'jsonous';
import { Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import { useRef } from 'react';
import { Link } from '../../../Resource/Types';
import TypeaheadSuggestionsDropdown from './TypeaheadSuggestionsDropdown';
import {
  TypeaheadResource,
  TypeaheadSelection,
  createExistingSelection,
  createNewSelection,
} from './Types';
import { useTypeaheadSuggestions } from './UseTypeaheadSuggestions';

interface Props<T> {
  value: string;
  onChange: (value: string) => void;
  onSelectionChange?: (selection: TypeaheadSelection<T>) => void;
  onBlur: () => void;
  placeholder: string;
  className: string;
  endpoint: Maybe<Link>;
  decoder: Decoder<TypeaheadResource<T>>;
  renderSuggestion: (suggestion: T, index: number, isSelected: boolean) => React.ReactNode;
  allowCreate?: boolean;
  createLabel?: (query: string) => string;
  minQueryLength?: number;
  debounceMs?: number;
  inputType?: string;
}

function TypeaheadInput<T>({
  value,
  onChange,
  onSelectionChange,
  onBlur,
  placeholder,
  className,
  endpoint,
  decoder,
  renderSuggestion,
  allowCreate = false,
  createLabel,
  minQueryLength = 2,
  debounceMs = 500,
  inputType = 'text',
}: Props<T>) {
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    suggestions,
    showSuggestions,
    isLoading,
    handleInputChange,
    handleSuggestionClick: baseSuggestionClick,
    handleBlur: handleSuggestionsBlur,
    handleFocus,
  } = useTypeaheadSuggestions<T>(endpoint, onChange, onBlur, decoder, minQueryLength, debounceMs);

  const handleSuggestionClick = (suggestion: T) => {
    baseSuggestionClick(suggestion);
    if (onSelectionChange) {
      onSelectionChange(createExistingSelection(suggestion));
    }
  };

  const handleCreateClick = (query: string) => {
    if (onSelectionChange) {
      onSelectionChange(createNewSelection(query));
    }
  };

  // Extract flex classes for the container, and styling classes for the input
  const containerClasses = className
    .split(' ')
    .filter((cls) => cls.startsWith('flex') || cls === 'w-full')
    .join(' ');

  const inputClasses = className
    .split(' ')
    .filter((cls) => !cls.startsWith('flex') && cls !== 'w-full')
    .join(' ');

  return (
    <div ref={containerRef} className={`relative ${containerClasses}`}>
      <input
        type={inputType}
        value={value}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => handleFocus(value)}
        onBlur={handleSuggestionsBlur}
        placeholder={placeholder}
        className={`w-full ${inputClasses}`}
      />

      {showSuggestions && (
        <TypeaheadSuggestionsDropdown
          suggestions={suggestions}
          isLoading={isLoading}
          onSuggestionClick={handleSuggestionClick}
          onCreateClick={allowCreate ? handleCreateClick : undefined}
          inputWidth={containerRef.current?.offsetWidth}
          isActive={showSuggestions}
          renderSuggestion={renderSuggestion}
          allowCreate={allowCreate}
          createLabel={createLabel ? createLabel(value) : undefined}
          query={value}
        />
      )}
    </div>
  );
}

export default observer(TypeaheadInput);
